import { useState, useEffect } from 'react'
import { apiUrl } from '../utils/apiBase'
import { QRCodeSVG } from 'qrcode.react'
import Lottie from './Lottie'
import GlassOverlay from './GlassOverlay'
import CloseGlyph from './CloseGlyph'
import ConfirmModal from './ConfirmModal'
import AdminFilterSelect from './AdminFilterSelect'
import LiquidGlassButton from './LiquidGlassButton'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import './ShareCartModal.css'

const EXPIRY_OPTIONS = [
  { value: 1, label: '1 jour' },
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
  { value: null, label: 'Sans expiration' },
]

function CopyGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect x="8" y="8" width="12" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M5 12.5l5 5L19 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function DownloadGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M12 4v10m0 0l-4-4m4 4l4-4M5 19h14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RefreshGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M20 12a8 8 0 1 1-2.2-5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M20 5v5h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CreateLinkGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M10 13a5 5 0 0 0 7.1.1l1.8-1.8a5 5 0 0 0-7.1-7.1L10.4 5.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14 11a5 5 0 0 0-7.1-.1L5.1 12.7a5 5 0 0 0 7.1 7.1l1.4-1.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 8v8M8 12h8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function RevokeGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M7 11V8a5 5 0 0 1 10 0v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <rect x="6" y="11" width="12" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 14.5v2.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function EyeGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}

function ClockGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v4.4l2.6 1.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function ShareIconButton({
  label,
  onClick,
  className = '',
  busy = false,
  disabled = false,
  children,
}) {
  return (
    <LiquidGlassButton
      type="button"
      className={`share-icon-btn${className ? ` ${className}` : ''}${busy ? ' is-busy' : ''}`}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.(event)
      }}
      aria-label={label}
      title={label}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
    >
      {children}
    </LiquidGlassButton>
  )
}

function ShareCartModal({ isOpen, onClose, cartId, cartName }) {
  const [shareLink, setShareLink] = useState(null)
  const [allLinks, setAllLinks] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expiresInDays, setExpiresInDays] = useState(7)
  const [copied, setCopied] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [stats, setStats] = useState(null)
  const [statsAnimation, setStatsAnimation] = useState(null)
  const [statsRefreshing, setStatsRefreshing] = useState(false)
  const [showLinkForm, setShowLinkForm] = useState(false)
  const [selectedLinkId, setSelectedLinkId] = useState(null)
  const [trashAnimation, setTrashAnimation] = useState(null)
  const [penAnimation, setPenAnimation] = useState(null)
  const [editingLinkId, setEditingLinkId] = useState(null)
  const [linkName, setLinkName] = useState('')
  const [newLinkName, setNewLinkName] = useState('')
  const [revokeTargetId, setRevokeTargetId] = useState(null)
  const [revoking, setRevoking] = useState(false)
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    fetch('/stats.json')
      .then((res) => res.json())
      .then((data) => setStatsAnimation(data))
      .catch((err) => console.error('Erreur chargement animation stats:', err))

    fetch('/Red_trash.json')
      .then((res) => res.json())
      .then((data) => setTrashAnimation(data))
      .catch((err) => console.error('Erreur chargement animation trash:', err))

    fetch('/Pen3D.json')
      .then((res) => res.json())
      .then((data) => setPenAnimation(data))
      .catch((err) => console.error('Erreur chargement animation pen:', err))
  }, [])

  useEffect(() => {
    if (isOpen && cartId) {
      loadExistingLink()
    }
  }, [isOpen, cartId])

  useEffect(() => {
    setShowStats(false)
    setStats(null)
    setCopied(false)
  }, [selectedLinkId])

  const loadExistingLink = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(
        apiUrl(`/api/shared-links?cart_id=${cartId}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.ok) {
        const links = await response.json()
        const activeLinks = links.filter((link) => link.is_active && link.cart_id === cartId)
        setAllLinks(activeLinks)

        if (activeLinks.length > 0) {
          setShareLink(activeLinks[0])
          setSelectedLinkId(activeLinks[0].id)
        } else {
          setShareLink(null)
          setSelectedLinkId(null)
          setShowLinkForm(true)
        }
      } else {
        setShowLinkForm(true)
      }
    } catch (err) {
      console.error('[ShareCartModal] Erreur:', err)
      setShowLinkForm(true)
    } finally {
      setLoading(false)
    }
  }

  const createShareLink = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(
        apiUrl(`/api/carts/${cartId}/share`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            cart_id: cartId,
            expires_in_days: expiresInDays,
            name: newLinkName || null,
          }),
        },
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Erreur lors de la création du lien')
      }

      const data = await response.json()
      setShareLink(data)
      setSelectedLinkId(data.id)
      setNewLinkName('')
      await loadExistingLink()
      setShowLinkForm(false)
    } catch (err) {
      console.error('[ShareCartModal] Erreur:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (!shareLink) return
    const value = shareLink.public_url
    let copiedText = false
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
        copiedText = true
      }
    } catch {
      copiedText = false
    }
    if (!copiedText) {
      try {
        const field = document.querySelector('.share-cart-modal .share-link-input')
        if (field) {
          field.focus()
          field.select()
          copiedText = document.execCommand('copy')
        }
      } catch (err) {
        console.error('Erreur lors de la copie:', err)
      }
    }
    if (!copiedText) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg')
    if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const pngFile = canvas.toDataURL('image/png')

      const downloadLink = document.createElement('a')
      downloadLink.download = `qr-code-${cartName}.png`
      downloadLink.href = pngFile
      downloadLink.click()
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  const loadStats = async () => {
    if (!shareLink) return
    setStatsRefreshing(true)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(
        apiUrl(`/api/shared-links/${shareLink.id}/stats`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.ok) {
        const data = await response.json()
        setStats(data)
        setShowStats(true)
      }
    } catch (err) {
      console.error('Erreur lors du chargement des stats:', err)
    } finally {
      setStatsRefreshing(false)
    }
  }

  const deactivateLink = async (linkId) => {
    if (!linkId) return
    setRevoking(true)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(
        apiUrl(`/api/shared-links/${linkId}/deactivate`),
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.ok) {
        setRevokeTargetId(null)
        await loadExistingLink()
      }
    } catch (err) {
      console.error('Erreur lors de la désactivation:', err)
    } finally {
      setRevoking(false)
    }
  }

  const generateNewLink = () => {
    setShowLinkForm(true)
  }

  const startEditingLinkName = (link) => {
    setEditingLinkId(link.id)
    setLinkName(link.name || '')
  }

  const saveLinkName = async (linkId) => {
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(
        apiUrl(`/api/shared-links/${linkId}`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: linkName }),
        },
      )

      if (response.ok) {
        await loadExistingLink()
        setEditingLinkId(null)
        setLinkName('')
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour du nom:', err)
    }
  }

  const cancelEditing = () => {
    setEditingLinkId(null)
    setLinkName('')
  }

  const expiryLabel = shareLink?.expires_at
    ? `Expire le ${new Date(shareLink.expires_at).toLocaleDateString('fr-FR')}`
    : 'Sans expiration'

  return (
    <>
      <GlassOverlay
        isOpen={isOpen}
        onClose={onClose}
        labelledBy="share-cart-title"
        panelClassName="share-cart-modal"
        followPointer={false}
        zIndex={1300}
      >
        <div className="share-cart-modal-inner">
          <div className="share-modal-header">
            <h2 id="share-cart-title">Partager le panier</h2>
            <button type="button" className="share-modal-close" onClick={onClose} aria-label="Fermer">
              <CloseGlyph />
            </button>
          </div>

          <div className="share-modal-body">
            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Chargement...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>{error}</p>
                <ShareIconButton label="Réessayer" onClick={loadExistingLink}>
                  <RefreshGlyph />
                </ShareIconButton>
              </div>
            )}

            {showLinkForm && !loading && (
              <section className="share-section share-create-section">
                <h3>Créer un nouveau lien de partage</h3>
                <div className="form-group">
                  <label htmlFor="link-name-input">Nom du destinataire (optionnel)</label>
                  <input
                    id="link-name-input"
                    type="text"
                    value={newLinkName}
                    onChange={(e) => setNewLinkName(e.target.value)}
                    className="link-name-input-form"
                    placeholder="Ex: Maman, Papa, Amis..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="expiry-select">Durée de validité</label>
                  <AdminFilterSelect
                    id="expiry-select"
                    className="share-expiry-select"
                    label="Durée de validité"
                    value={expiresInDays}
                    options={EXPIRY_OPTIONS}
                    onChange={setExpiresInDays}
                    disabled={loading}
                  />
                </div>
                <div className="share-create-actions">
                  <ShareIconButton
                    label="Générer le lien"
                    onClick={createShareLink}
                    disabled={loading}
                  >
                    <CreateLinkGlyph />
                  </ShareIconButton>
                </div>
              </section>
            )}

            {!loading && allLinks.length > 0 && shareLink && (
              <>
                <section className="share-section" aria-labelledby="share-link-label">
                  <div className="share-section-head">
                    <label id="share-link-label" className="share-label">
                      {allLinks.length > 1 ? 'Lien sélectionné' : 'Lien de partage'}
                    </label>
                    <ShareIconButton
                      label={copied ? 'Lien copié' : 'Copier le lien'}
                      onClick={copyToClipboard}
                      className={copied ? 'is-copied' : ''}
                    >
                      {copied ? <CheckGlyph /> : <CopyGlyph />}
                    </ShareIconButton>
                  </div>
                  <div className="share-link-container">
                    <input
                      type="text"
                      value={shareLink.public_url}
                      readOnly
                      className="share-link-input"
                    />
                  </div>
                  <p className="expiry-info">{expiryLabel}</p>
                  {copied ? (
                    <p className="share-copy-feedback" role="status" aria-live="polite">Lien copié</p>
                  ) : null}
                  {!shareLink.is_active && (
                    <p className="inactive-info">Ce lien est désactivé</p>
                  )}

                  {allLinks.length > 1 && (
                    <div className="links-list">
                      {allLinks.map((link) => (
                        <div
                          key={link.id}
                          className={`link-item ${selectedLinkId === link.id ? 'selected' : ''}`}
                          onClick={() => {
                            setShareLink(link)
                            setSelectedLinkId(link.id)
                          }}
                        >
                          <div className="link-item-info">
                            {editingLinkId === link.id ? (
                              <div className="link-name-edit">
                                <input
                                  type="text"
                                  value={linkName}
                                  onChange={(e) => setLinkName(e.target.value)}
                                  className="link-name-input"
                                  placeholder="Nom du destinataire..."
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.stopPropagation()
                                      saveLinkName(link.id)
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      cancelEditing()
                                    }
                                  }}
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    saveLinkName(link.id)
                                  }}
                                  className="link-name-save"
                                  title="Enregistrer"
                                  aria-label="Enregistrer"
                                >
                                  <CheckGlyph />
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    cancelEditing()
                                  }}
                                  className="link-name-cancel"
                                  title="Annuler"
                                  aria-label="Annuler"
                                >
                                  <CloseGlyph />
                                </button>
                              </div>
                            ) : (
                              <div className="link-name-display">
                                <span className="link-item-name">
                                  {link.name || 'Sans nom'}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    startEditingLinkName(link)
                                  }}
                                  className="link-name-edit-btn"
                                  title="Modifier le nom"
                                  aria-label="Modifier le nom"
                                >
                                  {penAnimation && !reducedMotion ? (
                                    <Lottie
                                      animationData={penAnimation}
                                      loop
                                      autoplay
                                      style={{ width: 20, height: 20 }}
                                    />
                                  ) : (
                                    <CreateLinkGlyph />
                                  )}
                                </button>
                              </div>
                            )}
                            <span className="link-item-token">{link.token.substring(0, 12)}…</span>
                            <span className="link-item-date">
                              {link.expires_at
                                ? `Expire le ${new Date(link.expires_at).toLocaleDateString('fr-FR')}`
                                : 'Sans expiration'}
                            </span>
                            <span className="link-item-views">
                              {link.view_count} vue{link.view_count > 1 ? 's' : ''}
                            </span>
                          </div>
                          <ShareIconButton
                            label="Révoquer ce lien"
                            className="is-danger"
                            onClick={(event) => {
                              event.stopPropagation()
                              setRevokeTargetId(link.id)
                            }}
                          >
                            {trashAnimation && !reducedMotion ? (
                              <Lottie
                                animationData={trashAnimation}
                                loop
                                autoplay
                                style={{ width: 22, height: 22 }}
                              />
                            ) : (
                              <RevokeGlyph />
                            )}
                          </ShareIconButton>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="share-section share-qr-section" aria-labelledby="share-qr-label">
                  <div className="share-section-head">
                    <label id="share-qr-label" className="share-label">QR Code</label>
                    <ShareIconButton label="Télécharger le QR code" onClick={downloadQRCode}>
                      <DownloadGlyph />
                    </ShareIconButton>
                  </div>
                  <div className="qr-code-container">
                    <QRCodeSVG
                      id="qr-code-svg"
                      value={shareLink.public_url}
                      size={176}
                      level="H"
                      includeMargin
                    />
                  </div>
                </section>

                <section className="share-section share-stats-section" aria-labelledby="share-stats-label">
                  <div className="share-section-head">
                    <label id="share-stats-label" className="share-label">Statistiques</label>
                    <ShareIconButton
                      label="Actualiser les statistiques"
                      onClick={loadStats}
                      className={statsRefreshing ? 'is-refreshing' : ''}
                      busy={statsRefreshing}
                    >
                      <RefreshGlyph />
                    </ShareIconButton>
                  </div>
                  {showStats && stats ? (
                    <div className="stats-container">
                      <div className="stat-item">
                        <span className="stat-icon" aria-hidden="true">
                          {statsAnimation && !reducedMotion ? (
                            <Lottie
                              animationData={statsAnimation}
                              loop
                              autoplay
                              style={{ width: 36, height: 36 }}
                            />
                          ) : (
                            <EyeGlyph />
                          )}
                        </span>
                        <div className="stat-details">
                          <span className="stat-value">{stats.total_views}</span>
                          <span className="stat-label">Visualisations</span>
                        </div>
                      </div>
                      {stats.last_viewed_at && (
                        <div className="stat-item">
                          <span className="stat-icon" aria-hidden="true"><ClockGlyph /></span>
                          <div className="stat-details">
                            <span className="stat-value">
                              {new Date(stats.last_viewed_at).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="stat-label">Dernière visite</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="stats-placeholder">
                      <span className="stat-icon" aria-hidden="true">
                        {statsAnimation && !reducedMotion ? (
                          <Lottie
                            animationData={statsAnimation}
                            loop
                            autoplay
                            style={{ width: 28, height: 28 }}
                          />
                        ) : (
                          <EyeGlyph />
                        )}
                      </span>
                      <div className="stat-details">
                        <span className="stat-value">{shareLink.view_count}</span>
                        <span className="stat-label">
                          visualisation{shareLink.view_count > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  )}
                </section>

                <section className="share-actions" aria-label="Actions du lien">
                  <ShareIconButton label="Créer un autre lien" onClick={generateNewLink}>
                    <CreateLinkGlyph />
                  </ShareIconButton>
                  {shareLink.is_active && (
                    <ShareIconButton
                      label="Révoquer le lien actuel"
                      className="is-danger"
                      onClick={() => setRevokeTargetId(shareLink.id)}
                    >
                      <RevokeGlyph />
                    </ShareIconButton>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      </GlassOverlay>

      <ConfirmModal
        isOpen={Boolean(revokeTargetId)}
        onClose={() => {
          if (!revoking) setRevokeTargetId(null)
        }}
        onConfirm={() => deactivateLink(revokeTargetId)}
        variant="delete"
        title="Révoquer ce lien ?"
        description="Le lien cessera d’être accessible. Cette action ne peut pas être annulée."
        confirmLabel="Révoquer"
        cancelLabel="Annuler"
        busy={revoking}
        zIndex={1520}
      />
    </>
  )
}

export default ShareCartModal
